// Content & scoring health, the platform owner's diagnostics, kept off the
// main admin page. Content inventory (is everything seeded?) and the W/S
// scoring queue (is grading flowing?).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

function Th({ children }) {
  return <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{children}</th>;
}

export default function AdminOps() {
  const [inventory, setInventory] = useState(null);
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [inv, sm] = await Promise.all([api.adminContentInventory(), api.adminSubmissions({ limit: 25 })]);
        setInventory(inv.data); setSubs(sm.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load ops data.');
      }
    })();
  }, []);

  return (
    <AppShell>
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="ops-back">
        <ArrowLeft className="h-4 w-4" /> Platform admin
      </Link>
      <p className="mono-stat mt-6">platform admin</p>
      <h1 className="h-display mt-2 text-3xl">Content &amp; scoring health</h1>
      <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-slate-500">
        Diagnostics only. Confirms content is seeded and the writing/speaking scoring queue is moving.
      </p>
      {error && <p className="mt-4 border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-900">{error}</p>}

      <section className="mt-10">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-900">Content inventory</h2>
          {inventory && (
            <p className="mono-stat" data-testid="admin-inventory-totals">
              units {Object.entries(inventory.units || {}).map(([k, v]) => `${k} ${v}`).join(' · ')}
            </p>
          )}
        </div>
        {!inventory ? <p className="mt-4 text-sm text-slate-400">Loading…</p> : (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100"><Th>Exam</Th><Th>Section</Th><Th>Task type</Th><Th>Sets</Th><Th>Questions</Th><Th>Audio</Th><Th>Mock forms</Th></tr></thead>
              <tbody>
                {inventory.items.map((r) => (
                  <tr key={`${r.exam}-${r.task_type}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2">{r.exam.toUpperCase()}</td>
                    <td className="px-4 py-2 capitalize">{r.section}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.task_type}</td>
                    <td className="px-4 py-2 tabular-nums">{r.sets}</td>
                    <td className="px-4 py-2 tabular-nums">{r.questions}</td>
                    <td className="px-4 py-2 tabular-nums text-slate-500">{r.with_audio > 0 ? `${r.with_audio}/${r.sets}` : ''}</td>
                    <td className="px-4 py-2 text-xs tabular-nums text-slate-500">{r.mock_forms.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-12">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-900">Writing &amp; speaking scoring queue</h2>
          {subs && <p className="mono-stat" data-testid="admin-subs-counts">{Object.entries(subs.by_status || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || 'empty'}</p>}
        </div>
        {!subs ? <p className="mt-4 text-sm text-slate-400">Loading…</p> : subs.submissions.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No submissions yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100"><Th>When</Th><Th>Exam</Th><Th>Task</Th><Th>Words</Th><Th>Status</Th><Th>Queue reason</Th></tr></thead>
              <tbody>
                {subs.submissions.map((s) => (
                  <tr key={s.submission_id} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2 tabular-nums text-slate-500">{s.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-2">{s.exam.toUpperCase()}</td>
                    <td className="px-4 py-2 font-mono text-xs">{s.task_type}</td>
                    <td className="px-4 py-2 tabular-nums">{s.word_count ?? ''}</td>
                    <td className={cn('px-4 py-2 font-medium', s.status === 'scored' ? 'text-emerald-600' : s.status === 'gate_zeroed' ? 'text-red-600' : 'text-slate-500')}>{s.status}</td>
                    <td className="px-4 py-2 text-slate-400">{s.queue_reason || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  );
}
